<template>
  <div class="cv-root">
    <!-- Add New Comment -->
    <div class="cv-composer">
      <textarea
        v-model="newComment"
        class="cv-textarea"
        placeholder="Add a comment…"
        rows="3"
      ></textarea>
      <button class="cv-submit" :disabled="!newComment.trim()" @click="addComment">
        <div class="cv-btn-tick"></div>
        <span>Post</span>
        <div class="cv-btn-tick"></div>
      </button>
    </div>

    <!-- All Comments -->
    <div class="cv-list-wrap">
      <div v-if="comments.length" class="cv-list">
        <CommentThread
          v-for="comment in comments"
          :key="comment.id"
          :comment="comment"
          :post-id="postID"
          :parent-path="`${collectionName}/${postID}/comments`"
        />
      </div>

      <div v-else class="cv-empty">
        <p>No comments yet. Be the first to comment!</p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  collection,
  addDoc,
  getDoc,
  getDocs,
  doc,
} from "firebase/firestore";
import { db, auth } from "@/firebase";
import CommentThread from "@/components/CommentThread.vue";

export default {
  name: "CommentsView",
  components: {
    CommentThread,
  },
  props: {
    postID: {
      type: String,
      required: true,
    },
    // Which top-level collection this post lives in — "posts" for chapter
    // discussion posts, "explore" for recommendations and travel plans.
    collectionName: {
      type: String,
      default: "posts",
    },
  },
  data() {
    return {
      comments: [],
      newComment: "",
      defaultUserImage: "/default-user.jpg",
    };
  },
  methods: {
    async fetchComments() {
      try {
        const snap = await getDocs(
          collection(db, `${this.collectionName}/${this.postID}/comments`)
        );
        this.comments = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      } catch (error) {
        console.error("Failed to fetch comments:", error);
      }
    },
    async addComment() {
      const user = auth.currentUser;
      if (!user || !this.newComment.trim()) {
        console.warn("Missing user or comment text");
        return;
      }

      try {
        const userRef = doc(db, "user", user.uid);
        const userSnap = await getDoc(userRef);
        const userData = userSnap.exists() ? userSnap.data() : {};

        const comment = {
          userID: user.uid,
          userName: userData.fullName || "Anonymous",
          userProfileImageURL: userData.image || this.defaultUserImage,
          text: this.newComment.trim(),
          likes: [],
          timestamp: new Date(),
        };

        await addDoc(collection(db, `${this.collectionName}/${this.postID}/comments`), comment);
        this.newComment = "";
        await this.fetchComments();
      } catch (err) {
        console.error("Error adding comment:", err);
      }
    },
  },
  mounted() {
    this.fetchComments();
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

.cv-root { font-family: 'Montserrat', sans-serif; padding: 24px 28px 32px; }

/* ── COMPOSER ── */
.cv-composer { display: flex; flex-direction: column; gap: 14px; margin-bottom: 28px; }
.cv-textarea {
  width: 100%; background: rgba(210,175,100,0.03); border: 1px solid rgba(210,175,100,0.18);
  padding: 14px; color: #edeae2; font-family: 'Montserrat', sans-serif; font-size: 14px; font-weight: 300;
  outline: none; caret-color: rgba(210,175,100,0.8); resize: vertical; transition: border-color 0.2s;
}
.cv-textarea::placeholder { color: rgba(237,232,223,0.2); font-style: italic; }
.cv-textarea:focus { border-color: rgba(210,175,100,0.45); }
.cv-submit {
  align-self: flex-end; display: flex; align-items: center; gap: 12px;
  font-family: 'Montserrat', sans-serif; font-size: 9.5px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase;
  color: rgba(210,175,100,0.9); background: rgba(210,175,100,0.08); border: 1px solid rgba(210,175,100,0.5);
  padding: 12px 22px; cursor: pointer; transition: background 0.2s, border-color 0.2s, opacity 0.2s;
}
.cv-submit:hover:not(:disabled) { background: rgba(210,175,100,0.16); border-color: rgba(210,175,100,0.75); }
.cv-submit:disabled { opacity: 0.3; cursor: not-allowed; }
.cv-btn-tick { width: 14px; height: 1px; background: rgba(210,175,100,0.4); flex-shrink: 0; }

/* ── LIST ── */
.cv-list-wrap { border-top: 1px solid rgba(210,175,100,0.08); padding-top: 20px; }
.cv-list { display: flex; flex-direction: column; gap: 4px; }
.cv-empty {
  text-align: center; padding: 32px 0;
  font-size: 11px; font-weight: 400; letter-spacing: 1.5px; text-transform: uppercase;
  color: rgba(237,232,223,0.2);
}
</style>